import React, { useEffect, useMemo, useRef, useState } from "react";
import { api, API } from "../lib/api";
import { PageHeader, PageBody, StatusBadge, EmptyState } from "../components/Shared";
import { Link } from "react-router-dom";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Trash2, X, Upload, FileDown, CheckCircle2, AlertTriangle, FileText } from "lucide-react";
import { toast } from "sonner";

const STATUSES = ["all", "open", "in_progress", "completed", "cancelled"];

export default function Orders() {
  const [orders, setOrders] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [filters, setFilters] = useState({ order_status: "all", supplier_id: "all", sub_supplier_id: "all" });
  const [q, setQ] = useState("");

  const load = () => {
    const params = {};
    Object.entries(filters).forEach(([k, v]) => { if (v && v !== "all") params[k] = v; });
    return api.get("/orders", { params }).then((r) => setOrders(r.data));
  };

  useEffect(() => { api.get("/suppliers").then((r) => setSuppliers(r.data)); }, []);
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [filters]); // eslint-disable-line react-hooks/exhaustive-deps

  const del = async (e, o) => {
    e.preventDefault(); e.stopPropagation();
    if (!window.confirm(`Delete order ${o.order_number}? This cannot be undone.`)) return;
    try {
      await api.delete(`/orders/${o.id}`);
      toast.success(`Order ${o.order_number} deleted`);
      load();
    } catch { toast.error("Failed to delete"); }
  };

  const filtered = useMemo(() => {
    if (!q) return orders;
    const s = q.toLowerCase();
    return orders.filter((o) =>
      o.order_number?.toLowerCase().includes(s) ||
      o.offer_number?.toLowerCase().includes(s) ||
      o.customer_name?.toLowerCase().includes(s)
    );
  }, [orders, q]);

  const fileInputRef = useRef(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [proformaOpen, setProformaOpen] = useState(false);
  const downloadTemplate = async () => {
    try {
      const res = await fetch(`${API}/orders/import-template.xlsx`, { credentials: "include" });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = "orders-import-template.xlsx";
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(link.href), 5000);
    } catch { toast.error("Failed to download template"); }
  };
  const onFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setImporting(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/orders/import.xlsx", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImportResult({ ...data, filename: file.name });  // open modal
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Import failed");
    } finally { setImporting(false); }
  };
  const setF = (k, v) => setFilters((f) => ({ ...f, [k]: v }));
  const clearFilters = () => { setFilters({ order_status: "all", supplier_id: "all", sub_supplier_id: "all" }); setQ(""); };
  const hasFilters =
    filters.order_status !== "all" || filters.supplier_id !== "all" || filters.sub_supplier_id !== "all" || q;

  return (
    <>
      <PageHeader
        title="Orders"
        subtitle="Fulfilment"
        testid="orders-title"
        actions={
          <div className="flex items-center gap-2">
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xlsm"
              className="hidden"
              onChange={onFileChange}
              data-testid="orders-import-file"
            />
            <Button
              variant="outline"
              className="rounded-none h-9"
              onClick={downloadTemplate}
              data-testid="orders-download-template"
            >
              <FileDown className="w-4 h-4 mr-2" /> Template
            </Button>
            <Button
              variant="outline"
              className="rounded-none h-9 border-emerald-300 text-emerald-800 hover:bg-emerald-50"
              onClick={() => setProformaOpen(true)}
              disabled={orders.length === 0}
              data-testid="orders-create-proforma-btn"
            >
              <FileText className="w-4 h-4 mr-2" /> Create Proforma
            </Button>
            <Button
              className="rounded-none h-9 bg-blue-600 hover:bg-blue-700"
              disabled={importing}
              onClick={() => fileInputRef.current?.click()}
              data-testid="orders-import-btn"
            >
              <Upload className={`w-4 h-4 mr-2 ${importing ? "animate-pulse" : ""}`} />
              {importing ? "Importing…" : "Import Excel"}
            </Button>
          </div>
        }
      />
      <PageBody>
        <div className="bg-white border border-slate-300 p-4 mb-4 grid gap-3 md:grid-cols-5 items-end">
          <div className="md:col-span-2">
            <Label className="label-caps">Search</Label>
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Order #, offer #, customer…"
              className="rounded-none mt-1"
              data-testid="orders-search"
            />
          </div>
          <div>
            <Label className="label-caps">Status</Label>
            <Select value={filters.order_status} onValueChange={(v) => setF("order_status", v)}>
              <SelectTrigger className="rounded-none mt-1" data-testid="orders-filter-status"><SelectValue /></SelectTrigger>
              <SelectContent className="rounded-none">
                {STATUSES.map((s) => <SelectItem key={s} value={s}>{s === "all" ? "All" : s.replace("_", " ")}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="label-caps">Primary supplier</Label>
            <Select value={filters.supplier_id} onValueChange={(v) => setF("supplier_id", v)}>
              <SelectTrigger className="rounded-none mt-1" data-testid="orders-filter-supplier"><SelectValue /></SelectTrigger>
              <SelectContent className="rounded-none">
                <SelectItem value="all">All suppliers</SelectItem>
                {suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="label-caps">Sub-supplier</Label>
            <Select value={filters.sub_supplier_id} onValueChange={(v) => setF("sub_supplier_id", v)}>
              <SelectTrigger className="rounded-none mt-1" data-testid="orders-filter-sub-supplier"><SelectValue /></SelectTrigger>
              <SelectContent className="rounded-none">
                <SelectItem value="all">All sub-suppliers</SelectItem>
                {suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-5 flex items-center justify-between text-xs text-slate-500">
            <div>{filtered.length} order{filtered.length === 1 ? "" : "s"} shown</div>
            {hasFilters && (
              <Button
                variant="outline"
                size="sm"
                onClick={clearFilters}
                className="rounded-none h-7 text-xs"
                data-testid="orders-clear-filters"
              >
                <X className="w-3 h-3 mr-1" /> Clear filters
              </Button>
            )}
          </div>
        </div>

        {filtered.length === 0 ? (
          <EmptyState title={hasFilters ? "No orders match these filters" : "No orders yet"} hint={hasFilters ? "Try clearing filters." : "Convert items from an Offer to create an order."} />
        ) : (
          <div className="bg-white border border-slate-300">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-300"><tr className="text-left">
                <th className="py-2 px-3 label-caps">Order #</th>
                <th className="py-2 px-3 label-caps">From Offer</th>
                <th className="py-2 px-3 label-caps">Customer</th>
                <th className="py-2 px-3 label-caps text-right">Items</th>
                <th className="py-2 px-3 label-caps text-right">Qty</th>
                <th className="py-2 px-3 label-caps">Status</th>
                <th className="py-2 px-3 label-caps">Created</th>
                <th className="py-2 px-3 w-10"></th>
              </tr></thead>
              <tbody>
                {filtered.map((o) => (
                  <tr key={o.id} className="border-b border-slate-200 hover:bg-slate-50" data-testid={`order-row-${o.id}`}>
                    <td className="py-2 px-3 font-mono"><Link to={`/orders/${o.id}`} className="text-blue-700 hover:text-blue-900">{o.order_number}</Link></td>
                    <td className="py-2 px-3 font-mono text-slate-500">{o.offer_number || "-"}</td>
                    <td className="py-2 px-3">{o.customer_name}</td>
                    <td className="py-2 px-3 font-mono text-right">{o.item_count}</td>
                    <td className="py-2 px-3 font-mono text-right">{o.total_qty}</td>
                    <td className="py-2 px-3"><StatusBadge status={o.status} /></td>
                    <td className="py-2 px-3 font-mono text-slate-500">{o.created_at?.slice(0, 10)}</td>
                    <td className="py-2 px-3 text-right">
                      <button
                        onClick={(e) => del(e, o)}
                        className="text-slate-400 hover:text-red-600 transition-colors duration-200"
                        title="Delete order"
                        data-testid={`del-order-${o.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </PageBody>

      <ImportResultDialog result={importResult} onClose={() => setImportResult(null)} />
      <ProformaDialog
        open={proformaOpen}
        onClose={() => setProformaOpen(false)}
        orders={orders}
      />
    </>
  );
}

function ImportResultDialog({ result, onClose }) {
  if (!result) return null;
  const {
    filename, orders_created = 0, items_created = 0,
    customers_created = 0, suppliers_created = 0,
    rows_seen = 0, errors = [],
  } = result;
  const hasErrors = errors.length > 0;
  const successful = orders_created > 0;

  return (
    <Dialog open={!!result} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="rounded-none max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight flex items-center gap-2">
            {hasErrors && !successful ? (
              <><AlertTriangle className="w-5 h-5 text-red-600" /> Import failed</>
            ) : hasErrors ? (
              <><AlertTriangle className="w-5 h-5 text-amber-600" /> Imported with warnings</>
            ) : (
              <><CheckCircle2 className="w-5 h-5 text-emerald-600" /> Import complete</>
            )}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {filename && (
            <div className="text-xs text-slate-500 truncate">
              File: <span className="font-mono text-slate-700">{filename}</span>
            </div>
          )}

          {/* Summary grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <SummaryTile label="Rows seen" value={rows_seen} />
            <SummaryTile label="Orders created" value={orders_created} tone={successful ? "good" : "default"} />
            <SummaryTile label="Line items" value={items_created} />
            <SummaryTile label="Rows skipped" value={errors.length} tone={hasErrors ? "warn" : "default"} testid="import-result-skipped" />
          </div>
          {(customers_created > 0 || suppliers_created > 0) && (
            <div className="text-xs text-slate-500 flex flex-wrap gap-4">
              {customers_created > 0 && <span><strong className="text-slate-800">{customers_created}</strong> new customer{customers_created === 1 ? "" : "s"} auto-created</span>}
              {suppliers_created > 0 && <span><strong className="text-slate-800">{suppliers_created}</strong> new supplier{suppliers_created === 1 ? "" : "s"} auto-created</span>}
            </div>
          )}

          {/* Errors list */}
          {hasErrors && (
            <div>
              <div className="label-caps mb-2 text-amber-800">Skipped rows &amp; warnings</div>
              <div className="border border-amber-200 bg-amber-50 max-h-72 overflow-y-auto" data-testid="import-result-errors">
                <ul className="divide-y divide-amber-200">
                  {errors.map((err, i) => (
                    <li key={i} className="px-3 py-2 text-xs font-mono text-amber-900">{err}</li>
                  ))}
                </ul>
              </div>
              <div className="text-xs text-slate-500 mt-2">
                Fix these rows in your Excel and re-import (existing orders will still be skipped as duplicates).
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button onClick={onClose} className="rounded-none bg-slate-800 hover:bg-slate-900 text-white" data-testid="import-result-close">
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ProformaDialog({ open, onClose, orders }) {
  const [orderId, setOrderId] = useState("");
  const [tlRate, setTlRate] = useState("");
  const [vatOverride, setVatOverride] = useState("");
  const [targetCurrency, setTargetCurrency] = useState("TRY");
  const [notes, setNotes] = useState("");
  const [defaultVat, setDefaultVat] = useState(0.20);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/settings/tax").then((r) => {
      if (typeof r.data?.vat_rate === "number") setDefaultVat(r.data.vat_rate);
    }).catch(() => {});
    setOrderId(""); setTlRate(""); setVatOverride(""); setTargetCurrency("TRY"); setNotes("");
  }, [open]);

  const selected = orders.find((o) => o.id === orderId);

  const generate = async () => {
    // Accept both "0,86" (Turkish) and "0.86" decimal separators
    const rate = parseFloat((tlRate || "").replace(",", "."));
    if (!orderId) return toast.error("Select an order");
    if (!(rate > 0)) return toast.error("Enter a valid exchange rate");
    const vatNum = vatOverride === "" ? null : parseFloat((vatOverride || "").replace(",", "."));
    if (vatNum !== null && (isNaN(vatNum) || vatNum < 0 || vatNum > 100)) {
      return toast.error("VAT % must be between 0 and 100");
    }
    setBusy(true);
    try {
      const params = { tl_rate: rate, target_currency: targetCurrency };
      if (vatNum !== null) params.vat_rate = vatNum / 100;
      if (notes.trim()) params.notes = notes.trim();
      const res = await api.get(`/orders/${orderId}/proforma.pdf`, {
        params,
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Proforma_${selected?.order_number || orderId}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      toast.success("Proforma PDF generated");
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to generate proforma");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="rounded-none max-w-lg" data-testid="proforma-dialog">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight flex items-center gap-2">
            <FileText className="w-5 h-5 text-emerald-700" /> Create Proforma Invoice
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="label-caps">Order</Label>
            <Select value={orderId} onValueChange={setOrderId}>
              <SelectTrigger className="rounded-none mt-1" data-testid="proforma-order-select">
                <SelectValue placeholder="Select an order…" />
              </SelectTrigger>
              <SelectContent className="rounded-none max-h-72">
                {orders.map((o) => (
                  <SelectItem key={o.id} value={o.id} data-testid={`proforma-order-${o.id}`}>
                    <span className="font-mono">{o.order_number}</span>
                    <span className="text-slate-500"> · {o.customer_name} · {o.item_count} items</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selected && (
              <div className="text-xs text-slate-500 mt-1" data-testid="proforma-order-summary">
                Customer: <span className="font-mono text-slate-800">{selected.customer_name}</span>
                {" · "}Qty: <span className="font-mono text-slate-800">{selected.total_qty}</span>
              </div>
            )}
          </div>
          <div>
            <Label className="label-caps">Target Currency</Label>
            <Select value={targetCurrency} onValueChange={setTargetCurrency}>
              <SelectTrigger className="rounded-none mt-1" data-testid="proforma-target-currency"><SelectValue /></SelectTrigger>
              <SelectContent className="rounded-none">
                <SelectItem value="TRY" data-testid="proforma-target-TRY">TRY (TL)</SelectItem>
                <SelectItem value="USD" data-testid="proforma-target-USD">USD</SelectItem>
                <SelectItem value="EUR" data-testid="proforma-target-EUR">EUR</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="label-caps">{targetCurrency} Exchange Rate</Label>
              <Input
                type="text"
                inputMode="decimal"
                value={tlRate}
                onChange={(e) => setTlRate(e.target.value)}
                placeholder={targetCurrency === "TRY" ? "e.g. 40,15" : "e.g. 0,86"}
                className="rounded-none mt-1 font-mono"
                data-testid="proforma-tl-rate"
              />
              <div className="text-[10px] text-slate-500 mt-1">1 unit of order currency = X {targetCurrency} (dot or comma both OK)</div>
            </div>
            <div>
              <Label className="label-caps">KDV % (optional)</Label>
              <Input
                type="number"
                step="0.1"
                value={vatOverride}
                onChange={(e) => setVatOverride(e.target.value)}
                placeholder={`default ${(defaultVat * 100).toFixed(0)}`}
                className="rounded-none mt-1 font-mono"
                data-testid="proforma-vat-rate"
              />
              <div className="text-[10px] text-slate-500 mt-1">Blank = use saved rate ({(defaultVat * 100).toFixed(0)}%) · 0 = ihracat (KDV'siz)</div>
            </div>
          </div>
          <div>
            <Label className="label-caps">Notes <span className="text-slate-400 normal-case tracking-normal">(opsiyonel — PDF'te Payment Details üstünde görünür)</span></Label>
            <Textarea
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="örn. Ödeme 15 gün içinde yapılmalıdır, navlun alıcıya aittir…"
              className="rounded-none mt-1"
              data-testid="proforma-notes"
            />
          </div>
          <div className="bg-slate-50 border border-slate-200 p-3 text-xs text-slate-600 space-y-1">
            <div><strong>How it's calculated:</strong></div>
            <div>· Sum of all line totals in order currency</div>
            <div>· Total ({targetCurrency}) = subtotal × rate</div>
            <div>· Grand Total = Total ({targetCurrency}) × (1 + KDV%)</div>
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
            className="rounded-none"
            disabled={busy}
            data-testid="proforma-cancel"
          >Cancel</Button>
          <Button
            onClick={generate}
            className="rounded-none bg-emerald-700 hover:bg-emerald-800 text-white"
            disabled={busy || !orderId || !tlRate}
            data-testid="proforma-generate"
          >
            <FileText className="w-4 h-4 mr-2" /> {busy ? "Generating…" : "Generate PDF"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SummaryTile({ label, value, tone = "default", testid }) {
  const tones = {
    default: "text-slate-900",
    good: "text-emerald-700",
    warn: "text-amber-700",
  };
  return (
    <div className="p-3 border border-slate-300 bg-white" data-testid={testid}>
      <div className="label-caps text-slate-500 text-[10px]">{label}</div>
      <div className={`mt-1 font-mono text-2xl tracking-tight ${tones[tone]}`}>{value}</div>
    </div>
  );
}
